function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
//  Copyright   : 2018 semantics GmbH
//  Date        : $Date: 2026-02-20 12:58:13 +0100 (Fri, 20 Feb 2026) $
//  Author      : Dirk Rothe
//  Worker      : $Author: d.rothe $
//  Revision    : $Rev: 160911 $
//  Purpose     : templating sagas
// =============================================================================
// ---Third-party imports
import React, { Component } from 'react';
import now from 'performance-now';
import { Label, Icon, Button } from 'semantic-ui-react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
// ---Framework imports
import { map, filterItems, dict, size, callIfFunc } from 'ldash';
import connect from 'uix/connect';
import { decodeGo } from 'uix/templating';
import { Actions, Sel } from 'uix/registries';
import log from 'uix/logging';
import styles from 'uix/styles';
import { ensurePathPrefix } from 'uix/router';
import { useState, useApi, useSelector } from 'uix/hooks';
import { VIcon } from 'uix/graphics';
// ---Globals
const LEGACY_CLASSES = [
    'nav'
];
const decodeGoLazy = (isLazyPending, request, oldRequest, replacedQueryParams, domains, templatingcfgs, deploy, routingBeautify)=>{
    if (isLazyPending) {
        return {
            pathname: ''
        };
    } else {
        oldRequest = {
            ...oldRequest,
            query: {
                ...oldRequest.query,
                ...replacedQueryParams
            }
        };
        return decodeGo(request, oldRequest, domains, templatingcfgs, deploy, routingBeautify);
    }
};
class Link extends Component {
    static mergeProps(request, props) {
        let { dname, odid, classname, tabname, ctxid, id, query, updateQuery } = props;
        let mergeRequest = dict(filterItems({
            dname,
            odid,
            classname,
            tabname,
            ctxid,
            id,
            query,
            updateQuery
        }, (k, v)=>v !== undefined));
        return {
            ...request,
            ...mergeRequest
        };
    }
    static getDerivedStateFromProps(nextProps, prevState) {
        let { request, oldRequest, replacedQueryParams, domains, templatingcfgs, routingBeautify, deploy, initDomain, path, loading } = nextProps;
        if (!domains) {
            return null;
        }
        let disabled = Link.is_disabled(nextProps);
        if (!path && !disabled) {
            try {
                request = Link.mergeProps(request, nextProps);
                let isLazyPending = nextProps.lazy && !prevState.hovered;
                var decoded = decodeGoLazy(isLazyPending, request, oldRequest, replacedQueryParams, domains, templatingcfgs, deploy, routingBeautify);
            } catch (e) {
                if (e.code === 'E_DOMAIN_UNLOADED') {
                    let isLoading = loading[e.dname];
                    let ddata = domains.data[e.dname];
                    if (!ddata) {
                        log.warn('bad dname %o', e.dname);
                    } else if (!isLoading && !ddata.isGhost) {
                        initDomain(e.dname, 'link');
                    }
                } else {
                    log.warn('cannot decode %o', request);
                //~ throw e
                }
            }
        }
        return null;
    }
    render() {
        let { path, request, cut, children, oldRequest, replacedQueryParams, domains, templatingcfgs, go, deploy, title, external, target, onClick, onClicked, lazy, apiurl, // custom formatting
        style, className } = this.props;
        request = Link.mergeProps(request, this.props);
        let props = {
            className: clsx(className, cut && styles.cut),
            style,
            title,
            onMouseEnter: ()=>!this.state.hovered && this.setState({
                    hovered: true
                })
        };
        // if api is offline switch to <span> building instead of <a> building
        let disabled = Link.is_disabled(this.props);
        if (!path) {
            let onLinkClick = (e)=>{
                // e.stopPropagation();
                e.preventDefault();
                callIfFunc(onClicked, e);
                let customizedRequest = onClick ? onClick(request) : request;
                if (customizedRequest === undefined) {
                    // onClick without result
                    // BEWARE: connection a redux action will not trigger this case as it returns a action struct
                    // we could detect this via checking {type, payload, meta} here
                    go(request);
                } else if (customizedRequest) {
                    go(customizedRequest);
                }
            };
            let p = '';
            try {
                var decoded = decodeGoLazy(this.isLazyPending(), request, oldRequest, replacedQueryParams, domains, templatingcfgs);
                p = decoded.pathname;
            } catch (e) {
                if (e.code === 'E_DOMAIN_UNLOADED') {
                    return /*#__PURE__*/ _jsx("span", {
                        className: cut ? styles.cut : null,
                        style: {
                            ...props.style,
                            cursor: 'wait'
                        },
                        children: children
                    });
                } else {
                    return /*#__PURE__*/ _jsx("span", {
                        className: cut ? styles.cut : null,
                        style: {
                            color: 'red'
                        },
                        children: children
                    });
                }
            }
            // if target class/tab is not available in templatingcfg, switch to disabled (or mark?)
            if (!this.isTabAvailable(decoded)) {
                log.warn('disable link, view not available: ', decoded.pathname);
                disabled = true;
            }
            // build full url for href indicator
            if (request.keepQuery && oldRequest) {
                const str = map(oldRequest, (v, k)=>`${k}=${encodeURIComponent(v)}`).join('&');
                if (str) {
                    p += `?${str}`;
                }
            } else if (size(decoded.query) > 0) {
                let str = map(decoded.query, (v, k)=>`${k}=${encodeURIComponent(v)}`).join('&');
                p += `?${str}`;
            }
            p = ensurePathPrefix(deploy.pathPrefix, p);
            if (!external) {
                props = {
                    ...props,
                    href: p,
                    onClick: onLinkClick
                };
            } else {
                // external link
                props = {
                    ...props,
                    href: p,
                    target
                };
            }
        } else {
            // path given: external link
            props = {
                ...props,
                href: path,
                target
            };
        }
        let { icon, basic, size: suiSize, floated, as: elementType, secondary } = this.props;
        if ([
            'button',
            'label'
        ].includes(elementType) || icon || basic) {
            let content = /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    icon && /*#__PURE__*/ _jsx(Icon, {
                        name: icon
                    }),
                    children
                ]
            });
            let E = {
                button: Button,
                label: Label
            }[elementType] || Button;
            return /*#__PURE__*/ React.createElement(E, {
                ...props,
                floated,
                basic,
                secondary,
                compact: true,
                size: suiSize,
                children: content
            });
        } else {
            if (disabled) {
                let { href, onClick, ...remaining } = props;
                props = {
                    ...remaining,
                    className: clsx(props.className, styles.disabledLink)
                };
            }
            return /*#__PURE__*/ _jsx("a", {
                ...props,
                children: children
            });
        }
    }
    constructor(...args){
        super(...args), _define_property(this, "state", {
            hovered: false
        }), _define_property(this, "isTabAvailable", ({ namedParams, templatingcfg })=>{
            if (this.isLazyPending()) return true;
            let { classname, tabname } = namedParams;
            if (LEGACY_CLASSES.includes(classname)) {
                return true;
            }
            return !!templatingcfg.classes[classname]?.tabs[tabname];
        }), _define_property(this, "isLazyPending", ()=>this.props.lazy && !this.state.hovered);
    }
}
_define_property(Link, "defaultProps", {
    // direct path fed into go action
    path: '',
    // go action compatible request (dname, classname, tabname, id, ...)
    request: {},
    // merge templating params into request
    dname: undefined,
    odid: undefined,
    classname: undefined,
    title: null,
    tabname: undefined,
    ctxid: undefined,
    id: undefined,
    query: undefined,
    updateQuery: undefined,
    // react children elements - probably a text node
    children: null,
    // css related
    cut: false,
    style: undefined,
    // behavior
    disabled: false,
    lazy: false,
    external: false,
    // external link target if "path" is given
    // overwrite with null for open in current tab
    target: '_new'
});
_define_property(Link, "propTypes", {
    domains: PropTypes.object.isRequired
});
_define_property(Link, "storeMap", (root, props)=>({
        oldRequest: root.meta.request,
        replacedQueryParams: root.meta.replacedQueryParams,
        templatingcfgs: root.config.templating,
        domains: root.config.domains,
        routingBeautify: root.config.routingBeautify,
        loading: root.config.loading,
        deploy: root.config.deploy,
        api_offline: root.meta.state == 'API_OFFLINE',
        app_running: root.meta.state == 'APP_RUNNING',
        apiurl: Sel.apiurl(root)
    }));
_define_property(Link, "actionMap", {
    go: Actions.go,
    initDomain: Actions.initDomain
});
_define_property(Link, "is_disabled", (props)=>{
    let { disabled, api_offline, app_running } = props;
    return disabled || api_offline || !app_running;
});
let ConnectedLink = connect('Link', Link);
export { ConnectedLink as Link };
export const UnknownDomainLink = ({ children, ...props })=>{
    /* use these links only sparsly
  we should know the domain of a otid to keep the number of resolving calls low
  */ let [active, setActive] = useState(false);
    let { data, error } = useApi('getDomainId', {
        otid: props.id
    }, {
        condition: active
    });
    if (!data) {
        return /*#__PURE__*/ _jsx("a", {
            href: "#",
            onMouseEnter: ()=>setActive(true),
            children: children
        });
    }
    return /*#__PURE__*/ _jsx(ConnectedLink, {
        odid: data.odid,
        ...props,
        children: children
    });
};
export const ApiLink = ({ children, path, ...props })=>/*#__PURE__*/ _jsx(ConnectedLink, {
        path: useSelector(Sel.apiurl).href(path),
        ...props,
        children: children
    });
export const RawLink = ({ children, linkIcon, target, ...props })=>{
    let user = useSelector(Sel.user);
    if (user.client == 'kiosk') {
        // no new tabs in kiosk mode
        target = undefined;
    }
    return /*#__PURE__*/ _jsxs("a", {
        target: target,
        ...props,
        children: [
            children,
            ' ',
            linkIcon && /*#__PURE__*/ _jsx(VIcon, {
                style: {
                    fontSize: '.75em'
                },
                name: typeof linkIcon === 'string' ? linkIcon : 'link'
            })
        ]
    });
};
